import React, { useState, useEffect, useContext } from 'react';
import Rest from 'tools/rest';
import { InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  Button,
  Row,
  Col,
  Form,
  Input,
  Typography,
  Select,
  Switch,
  Modal,
} from 'antd';
import {
  find as _find,
  includes as _includes,
  map as _map,
  isEqual as _isEqual,
} from 'lodash';
import { toast } from 'react-toastify';
import { AbilityContext } from 'tools/ability';
import ChangePasswordForm from './change_password_form';

const { Text } = Typography;
let roles = [];
let departments = [];

const PHONE_PLACEHOLDER = '7 XXX XXX XX XX';

function normalizePhoneDigits(value) {
  let d = String(value || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('8')) {
    d = `7${d.slice(1)}`;
  }
  if (d.length === 10 && d.startsWith('9')) {
    d = `7${d}`;
  }
  return d;
}

function formatPhoneDisplay(value) {
  const d = normalizePhoneDigits(value);
  if (!d) return '';
  if (d.length <= 1) return d;
  if (d.length <= 4) return `${d[0]} ${d.slice(1)}`;
  if (d.length <= 7) return `${d[0]} ${d.slice(1, 4)} ${d.slice(4)}`;
  if (d.length <= 9) return `${d[0]} ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  return `${d[0]} ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7, 9)} ${d.slice(9)}`;
}

function isValidPhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return true;
  const d = normalizePhoneDigits(value);
  return /^\d{11}$/.test(d) && d.startsWith('7');
}

const ProfileForm = (props) => {
  const [user, setUser] = useState({
    id: null,
    name: null,
    email: null,
    role: null,
    department_id: null,
    active: true,
    phone: null,
    lb_manager_id: null,
    allow_two_factor_auth: false,
    password: null,
    check_password: null,
  })
  const [currentUser, setCurrentUser] = useState({})
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({})
  const [visibleChangePassword, setVisibleChangePassword] = useState(false)
  const [regenerateLoading, setRegenerateLoading] = useState(false)
  const context = useContext(AbilityContext)
  const can_manage = context.can('manage', 'User')

  const { user_id } = props

  useEffect(() => {
    loadRoles();
    loadDepartments();
    loadUser();
  }, []);

  const loadUser = () => {
    if (!user_id) {return}
    Rest.get(`/api/v1/users/${user_id}`).then((response) => {
      const current_user = {...user, ...response.data.user}
      if (current_user.phone) {
        current_user.phone = formatPhoneDisplay(current_user.phone);
      }
      setUser(current_user);
      setCurrentUser(current_user);
    });
  };

  const updateUser = () => {
    setErrors({})
    if (!isValidPhone(user.phone)) {
      setErrors({ phone: ['Укажите номер в формате 7 XXX XXX XX XX (11 цифр)'] });
      toast.error('Проверьте номер телефона');
      return;
    }
    const params = {
      user: user,
    }
    Rest.put(`/api/v1/users/${user_id}`, params).then(
      (response) => {
        const { user: saved } = response.data;
        const next = { ...saved };
        if (next.phone) {
          next.phone = formatPhoneDisplay(next.phone);
        }
        setUser(next);
        setCurrentUser(next);
        toast.success('Изменения сохранены');
      }).catch((e) => {
        setErrors(e.response.data.user.errors)
        toast.error('Ошибка сохранения');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createUser = () => {
    setErrors({})
    if (!isValidPhone(user.phone)) {
      setErrors({ phone: ['Укажите номер в формате 7 XXX XXX XX XX (11 цифр)'] });
      toast.error('Проверьте номер телефона');
      return;
    }
    const params = {
      user: user,
    }
    Rest.post(`/api/v1/users.json`, params).then(
      (response) => {
        const { user: saved } = response.data;
        const next = { ...saved };
        if (next.phone) {
          next.phone = formatPhoneDisplay(next.phone);
        }
        setUser(next);
        setCurrentUser(next);
        toast.success('Пользователь создан');
      }).catch((e) => {
        setErrors(e.response.data.user.errors)
        toast.error('Ошибка создания пользователя');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadRoles = () => {
    Rest.get(`/api/v1/directory/user_roles`).then((response) => {
      const { user_roles } = response.data;
      roles = _map(user_roles, (_, role) => {
        return { label: role, value: role }
      })
    });
  };

  const loadDepartments = () => {
    Rest.get(`/api/v1/departments.json`).then((response) => {
      departments = _map(response.data.departments, (department) => {
        return { label: department.name, value: department.id }
      })
    });
  };

  const regeneratePassword = () => {
    setRegenerateLoading(true);
    setErrors({});
    Rest.put(`/api/v1/users/${user.id}/regenerate_password`).then((response) => {
      toast.success('Пароль пересоздан и отправлен по SMS');
    }).catch((e) => {
      setErrors(e?.response?.data?.user?.errors || {});
      toast.error('Ошибка пересоздания пароля');
    }).finally(() => {
      setRegenerateLoading(false);
    });
  };

  const handleChangeText = (e) => {
    const text = e.target.value?.trim() ? e.target.value : null
    setUser({ ...user, [e.target.name]: text })
    setErrors({ ...errors, [e.target.name]: null })
  };

  const handleChangePhone = (e) => {
    const formatted = formatPhoneDisplay(e.target.value);
    setUser({ ...user, phone: formatted || null });
    setErrors({ ...errors, phone: null });
  };

  const handleChangeSelector = (value, option_name) => {
    setUser({ ...user, [option_name]: value });
  };

  return (
    <>
    {visibleChangePassword && user?.id &&
      <Modal
        title="Смена пароля"
        open={visibleChangePassword}
        onCancel={() => setVisibleChangePassword(false)}
        footer={false}
        width={'50%'}
      >
        <ChangePasswordForm
          userId={user.id}
          onSuccess={() => setVisibleChangePassword(false)}
        />
      </Modal>
    }
    <Form
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
    >
      <Row>
        {user.pass_is_old &&
          <div style={{ textAlign: 'center', margin: '0px 15%' }}>
            <Text style={{ fontSize: '22px' }} type='danger'>
              <b>Внимание! До 15 сентября </b> необходимо сменить пароль, иначе доступ к учетной записи будет заблокирован!
              <br />
              Новый пароль должен отвечать требованиям безопасности: сожержать хотя бы одну цифру, строчную и заглавную буквы, спецсимвол и быть длиной не менее 8 символов.
            </Text>
          </div>
        }
      </Row>
      <Form.Item
        label="Имя"
        help={errors?.name && errors?.name.join(", ")}
        validateStatus={errors?.name && "error"}
      >
        <Input
          controls={false}
          name="name"
          value={user?.name}
          onChange={handleChangeText}
        />
      </Form.Item>
      <Form.Item
        label="Логин"
        help={errors?.email && errors?.email.join(", ")}
        validateStatus={errors?.email && "error"}
      >
        <Input
          controls={false}
          name="email"
          value={user?.email}
          onChange={handleChangeText}
        />
      </Form.Item>
      <Form.Item
        label="Роль"
        help={errors?.role && errors?.role.join(", ")}
        validateStatus={errors?.role && "error"}
      >
        {can_manage ?
          <Select
            allowClear
            showSearch
            value={user.role == '' ? undefined : user.role}
            placeholder='роль'
            optionFilterProp="children"
            filterOption={(input, option) => _includes(option.label.toLowerCase(), input.toLowerCase())}
            options={roles}
            onChange={(value)=> {handleChangeSelector(value, 'role')}}
          />
          :
          <Text>{user.role}</Text>
        }
      </Form.Item>
      <Form.Item
        label="Отдел"
        help={errors?.department_id && errors?.department_id.join(", ")}
        validateStatus={errors?.department_id && "error"}
      >
        {can_manage ?
          <Select
            allowClear
            showSearch
            value={user.department_id == '' ? undefined : user.department_id}
            placeholder='отдел'
            optionFilterProp="children"
            filterOption={(input, option) => _includes(option.label.toLowerCase(), input.toLowerCase())}
            options={departments}
            onChange={(value)=> {handleChangeSelector(value, 'department_id')}}
          />
          :
          <Text>{_find(departments, { value: user.department_id })?.label}</Text>
        }
      </Form.Item>
      <Form.Item
        label="Телефон"
        help={errors?.phone && errors?.phone.join(", ")}
        validateStatus={errors?.phone && "error"}
      >
        <Input
          controls={false}
          name="phone"
          value={user?.phone ?? ''}
          onChange={handleChangePhone}
          placeholder={PHONE_PLACEHOLDER}
          inputMode="tel"
          autoComplete="tel"
        />
      </Form.Item>
      <Form.Item
        label="ID LB"
        help={errors?.lb_manager_id && errors?.lb_manager_id.join(", ")}
        validateStatus={errors?.lb_manager_id && "error"}
      >
        <Input
          controls={false}
          name="lb_manager_id"
          value={user?.lb_manager_id}
          onChange={handleChangeText}
        />
      </Form.Item>
      <Form.Item
        label="Статус"
        help={errors?.active && errors?.active.join(", ")}
        validateStatus={errors?.active && "error"}
      >
        <Switch
          style={{width: '120px'}}
          checked={user.active}
          checkedChildren={'Аткивен'}
          unCheckedChildren={'Не активен'}
          onChange={(checked) => { setUser({...user, active: checked}) }}
          disabled={!can_manage}
        />
      </Form.Item>
      <Form.Item
        label="2FA"
        help={errors?.allow_two_factor_auth && errors?.allow_two_factor_auth.join(", ")}
        validateStatus={errors?.allow_two_factor_auth && "error"}
      >
        <Switch
          style={{width: '180px'}}
          checked={Boolean(user.allow_two_factor_auth)}
          checkedChildren={'2FA разрешена'}
          unCheckedChildren={'2FA выключена'}
          onChange={(checked) => { setUser({...user, allow_two_factor_auth: checked}) }}
        />
      </Form.Item>
      <Row justify={'center'}>
        <Button
          style={{ width: '350px' }}
          type='primary'
          htmlType="submit"
          onClick={user?.id ? updateUser : createUser}
          disabled={_isEqual(user, currentUser)}
        >
          {user?.id ? 'Сохранить' : 'Создать'}
        </Button>
      </Row>
      {user?.id &&
        <Row justify={'center'} style={{ marginTop: '10px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button
              style={{ width: '170px' }}
              onClick={() => setVisibleChangePassword(true)}
            >
              Изменить пароль
            </Button>
            <Button
              style={{ width: '170px' }}
              onClick={regeneratePassword}
              loading={regenerateLoading}
            >
              Пересоздать пароль
            </Button>
          </div>
        </Row>
      }
    </Form>
    </>
  );
};

export default ProfileForm;
